import { useEffect, useState, useMemo } from "react";
import { api, API } from "../../lib/api";
import { Download, Search } from "lucide-react";

export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const r = await api.get("/admin/customers", { params: query ? { q: query } : {} });
      setItems(r.data.customers || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/admin/customers");
        if (!cancel) setItems(r.data.customers || []);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(q.trim());
  };

  const exportCsv = () => {
    // Use the same token we already store. Since this triggers a download, we open via a token-aware redirect.
    const token = localStorage.getItem("prize_admin_token");
    // Build URL with Authorization header by fetching as blob (browsers can't send headers via window.open)
    fetch(`${API}/admin/customers.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `prize-joias-clientes-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const counts = useMemo(() => ({
    total: items.length,
    google: items.filter((i) => i.origem === "Google").length,
    manual: items.filter((i) => i.origem === "Manual").length,
    pending: items.filter((i) => i.status === "Aguardando confirmação").length,
  }), [items]);

  return (
    <div className="max-w-6xl" data-testid="admin-customers">
      <p className="overline text-[#A8A8A8] mb-1">Base</p>
      <h1 className="font-display text-4xl mb-2">Clientes</h1>
      <p className="text-sm text-[#6B6B6B] mb-8">Lista unificada de cadastros via Google e manual.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Total" value={counts.total}/>
        <Stat label="Google" value={counts.google}/>
        <Stat label="Manual" value={counts.manual}/>
        <Stat label="Aguardando" value={counts.pending}/>
      </div>

      <div className="bg-white p-6 border border-[#E8E4DF]">
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
          <form onSubmit={onSearch} className="flex-1 flex items-center gap-2 border border-[#E8E4DF] px-3 py-2 bg-white" data-testid="search-form">
            <Search size={16} className="text-[#A8A8A8]"/>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nome ou e-mail…"
              className="flex-1 outline-none text-sm"
              data-testid="search-input"
            />
            <button type="submit" className="overline text-xs text-[#B8A88A] hover:text-[#8C7B5E]" data-testid="search-btn">Buscar</button>
          </form>
          <button onClick={exportCsv} className="btn-primary inline-flex items-center gap-2" data-testid="export-csv-btn">
            <Download size={14}/> Exportar CSV
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="text-left border-b border-[#E8E4DF]">
                <th className="py-3 pr-4 overline text-[#A8A8A8]">Nome</th>
                <th className="py-3 pr-4 overline text-[#A8A8A8]">E-mail</th>
                <th className="py-3 pr-4 overline text-[#A8A8A8]">Telefone</th>
                <th className="py-3 pr-4 overline text-[#A8A8A8]">Origem</th>
                <th className="py-3 pr-4 overline text-[#A8A8A8]">Cadastro</th>
                <th className="py-3 pr-4 overline text-[#A8A8A8]">Simulações este mês</th>
                <th className="py-3 overline text-[#A8A8A8]">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="py-10 text-center text-[#A8A8A8]">Carregando…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="py-10 text-center text-[#A8A8A8]" data-testid="customers-empty">Nenhum cliente encontrado.</td></tr>
              )}
              {items.map((c) => {
                const used = c.tryon_count_this_month ?? 0;
                const limit = c.tryon_limit ?? 30;
                const atLimit = used >= limit;
                return (
                <tr key={c.user_id} className="border-b border-[#F1EDE7] hover:bg-[#FAF9F7]" data-testid={`row-${c.user_id}`}>
                  <td className="py-3 pr-4">{c.name || "—"}</td>
                  <td className="py-3 pr-4">{c.email}</td>
                  <td className="py-3 pr-4">{c.phone || "—"}</td>
                  <td className="py-3 pr-4">
                    <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 ${c.origem === "Google" ? "bg-[#F0E9DA] text-[#8C7B5E]" : "bg-[#E8E4DF] text-[#4A4A4A]"}`}>
                      {c.origem}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-[#6B6B6B]">{fmtDate(c.created_at)}</td>
                  <td className="py-3 pr-4" data-testid={`tryon-usage-${c.user_id}`}>
                    <span className={`tabular-nums text-sm ${atLimit ? "text-[#B85050] font-medium" : "text-[#4A4A4A]"}`}>
                      {used}/{limit}
                    </span>
                  </td>
                  <td className="py-3">
                    <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 ${c.status === "Ativo" ? "bg-[#E8F0E4] text-[#3E6A1E]" : "bg-[#FFF4D6] text-[#7A5A1A]"}`}>
                      {c.status}
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-[#E8E4DF] p-5">
      <p className="overline text-[#A8A8A8] mb-1">{label}</p>
      <p className="font-display text-3xl">{value}</p>
    </div>
  );
}
